import React from "react";
import { getReferralMessage, hasReferralMatch } from "hhn-commons";
import {
  IBroadcastInfoMembers,
  IGroupInfo,
  IGroupInfoMembers,
  IInfoType,
  IPendingReferral,
  IUserInfo,
  USER_TYPE,
} from "@home-health-notify/domain";
import {
  AccordionContainer,
  AccordionDetails,
  StyledAccordionSummary,
  UserButton,
  UserListItem,
} from "../AddMember.style";
import UserCard from "./UserCard";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PatientsNearbyDetails from "./advanceSearch/list/PatientsNearbyDetails";
import { Box as CountsWrapper } from "@mui/material";
import { UserWorkload } from "./advanceSearch/list/userWorkload/UserWorkload";
import { ImmutableChat } from "@home-health-notify/domain/build/chat/ImmutableChat";
import AddUserListItemActions from "./AddUserListItemActions";

type AddUserListItemType = {
  userInfo: IUserInfo;
  enableReferrals?: boolean;
  existingMember?: IGroupInfoMembers[] | IBroadcastInfoMembers[];
  chat: ImmutableChat | undefined;
  existingGroup?: boolean;
  showWorkloadDetails?: boolean;
  showActions?: boolean;
  onItemClick?: (userId: string) => void;
  isSelected?: boolean;
};

const AddUserListItem = ({
  userInfo,
  enableReferrals,
  existingMember,
  chat,
  existingGroup,
  showWorkloadDetails = false,
  showActions = true,
  onItemClick,
  isSelected = false,
}: AddUserListItemType) => {
  const [expanded, setExpanded] = React.useState<boolean>(false);

  let groupInfo: IInfoType | undefined;
  let pendingReferrals: IPendingReferral[] = [];

  if (existingGroup) {
    groupInfo = chat?.chatInfo;
    pendingReferrals =
      (groupInfo && (groupInfo as IGroupInfo).pendingReferrals) || [];
  }

  const hasReferral = hasReferralMatch(pendingReferrals, userInfo.id);
  const referralMessage = getReferralMessage(pendingReferrals, userInfo.id);
  const isUserAlreadyInGroup = existingMember?.some(
    user => user.userId === userInfo.id,
  );
  const canSendReferral =
    (enableReferrals &&
      userInfo.type !== USER_TYPE.PATIENT &&
      userInfo.type !== USER_TYPE.PHYSICIAN) ||
    false;

  const isClickable = !!onItemClick;

  return (
    <React.Fragment key={userInfo.id}>
      <UserListItem data-testid="add-user-list-item">
        <UserButton
          disableRipple={!isClickable}
          disableTouchRipple={!isClickable}
          selected={isSelected}
          onClick={isClickable ? () => onItemClick(userInfo.id) : undefined}
          sx={isClickable ? { cursor: "pointer" } : undefined}
        >
          {showWorkloadDetails ? (
            <AccordionContainer
              expanded={expanded}
              onChange={
                isClickable
                  ? undefined
                  : (_, isExpanded) => setExpanded(isExpanded)
              }
            >
              <StyledAccordionSummary
                expandIcon={
                  <ExpandMoreIcon
                    onClick={
                      isClickable
                        ? (e: React.MouseEvent) => {
                            e.stopPropagation();
                            setExpanded(prev => !prev);
                          }
                        : undefined
                    }
                  />
                }
                onClick={isClickable ? (e: React.MouseEvent) => e.preventDefault() : undefined}
                aria-controls="accordion-content"
                data-testid="user-workload-accordion"
              >
                <UserCard
                  userInfo={userInfo}
                  isUserAlreadyInGroup={isUserAlreadyInGroup}
                  hasReferral={hasReferral}
                  referralMessage={referralMessage}
                />
                <PatientsNearbyDetails userInfo={userInfo} />
              </StyledAccordionSummary>
              <AccordionDetails>
                <CountsWrapper>
                  <UserWorkload
                    userInfo={userInfo}
                    loadUserWorkload={expanded}
                  />
                </CountsWrapper>
              </AccordionDetails>
            </AccordionContainer>
          ) : (
            <UserCard
              userInfo={userInfo}
              isUserAlreadyInGroup={isUserAlreadyInGroup}
              hasReferral={hasReferral}
              referralMessage={referralMessage}
            />
          )}
          {showActions && !isUserAlreadyInGroup && (
            <AddUserListItemActions
              userInfo={userInfo}
              hasReferral={hasReferral}
              canSendReferral={canSendReferral}
              chat={chat}
            />
          )}
        </UserButton>
      </UserListItem>
    </React.Fragment>
  );
};

export default AddUserListItem;
